/**
 * ShopInventory — generates a random selection of shop items
 * from attributes, skills, perks, and grid tiles.
 *
 * Prices scale with a multiplier that increases per purchase.
 */

import type { PlayerCharacter } from "../character/PlayerCharacter";
import { ALL_SKILLS } from "../character/skills/SkillCatalog";
import { ALL_PERKS } from "../character/skills/PerkCatalog";
import type { SkillDef } from "../character/skills/SkillDef";
import type { PerkDef } from "../character/skills/PerkDef";
import { MONO } from "../character/shapes";
import { ALL_ITEMS } from "../items";
import { weightedPickN, Weighted } from "../../framework/random";
import type { ShopItem } from "./ShopItem";

/** How much the price multiplier increases per purchase. */
const PRICE_ESCALATION = 0.2;

interface ShopEntry {
  item: ShopItem;
  weight: number;
  filter?: (player: PlayerCharacter) => boolean;
}

// --- Attribute items ---

const ATTRIBUTE_ITEMS: ShopEntry[] = [
  {
    weight: 10,
    item: {
      id: "shop_hp3", name: "+3 Max HP",
      description: "Increase maximum health by 3",
      color: "#22c55e", icon: "+", basePrice: 15,
      apply: (p) => { p.maxHp += 3; p.hp += 3; },
    },
  },
  {
    weight: 6,
    item: {
      id: "shop_hp5", name: "+5 Max HP",
      description: "Increase maximum health by 5",
      color: "#22c55e", icon: "+", basePrice: 25,
      apply: (p) => { p.maxHp += 5; p.hp += 5; },
    },
  },
  {
    weight: 10,
    item: {
      id: "shop_atk1", name: "+1 Attack",
      description: "All words deal +1 damage",
      color: "#ef4444", icon: "/", basePrice: 20,
      apply: (p) => { p.baseAttack += 1; },
    },
  },
  {
    weight: 3,
    item: {
      id: "shop_atk2", name: "+2 Attack",
      description: "All words deal +2 damage",
      color: "#ef4444", icon: "//", basePrice: 40,
      apply: (p) => { p.baseAttack += 2; },
    },
  },
  {
    weight: 3,
    filter: (p) => p.maxAp < 5,
    item: {
      id: "shop_ap", name: "+1 Max AP",
      description: "Extra action point each turn",
      color: "#fbbf24", icon: "O", basePrice: 50,
      apply: (p) => { p.maxAp += 1; },
    },
  },
  {
    weight: 8,
    filter: (p) => p.hp < p.maxHp,
    item: {
      id: "shop_heal", name: "Full Heal",
      description: "Restore all HP",
      color: "#3b82f6", icon: "H", basePrice: 10,
      apply: (p) => { p.hp = p.maxHp; },
    },
  },
];

// --- Grid tile ---

const GRID_ITEM: ShopEntry = {
  weight: 6,
  item: {
    id: "shop_tile", name: "+1 Grid Tile",
    description: "Expand the letter grid",
    color: "#22c55e", icon: "+", basePrice: 30,
    apply: (p) => { p.shapes.add(MONO); },
    gridPlacement: true,
  },
};

// --- Build skill/perk items from catalogs ---

function buildSkillItems(): ShopEntry[] {
  return ALL_SKILLS.map((def: SkillDef): ShopEntry => ({
    weight: def.weight,
    filter: (p) => {
      const inst = p.skills.getSkill(def.id);
      return !inst || inst.canLevelUp;
    },
    item: {
      id: `shop_skill_${def.id}`,
      name: def.name,
      description: def.description,
      color: def.color,
      icon: def.icon,
      basePrice: 25,
      apply: (p) => { p.skills.addSkill(def); },
    },
  }));
}

function buildPerkItems(): ShopEntry[] {
  return ALL_PERKS.map((def: PerkDef): ShopEntry => ({
    weight: def.weight,
    filter: (p) => {
      const inst = p.skills.getPerk(def.id);
      if (!inst) return true;
      return inst.canStack;
    },
    item: {
      id: `shop_perk_${def.id}`,
      name: def.name,
      description: def.description,
      color: def.color,
      icon: def.icon,
      basePrice: 35,
      apply: (p) => { p.skills.addPerk(def); },
    },
  }));
}

// --- Consumable items ---

function buildConsumableItems(): ShopEntry[] {
  return ALL_ITEMS.map(def => ({
    weight: 8,
    item: {
      id: `shop_item_${def.id}`,
      name: `+1 ${def.name}`,
      description: def.description,
      color: def.color,
      icon: def.icon,
      basePrice: 15,
      apply: (p) => { p.items.add(def.id); },
    },
  }));
}

// --- Full pool ---

const SKILL_ITEMS = buildSkillItems();
const PERK_ITEMS = buildPerkItems();
const CONSUMABLE_ITEMS = buildConsumableItems();

const ALL_ENTRIES: ShopEntry[] = [
  ...ATTRIBUTE_ITEMS,
  ...SKILL_ITEMS,
  ...PERK_ITEMS,
  ...CONSUMABLE_ITEMS,
  GRID_ITEM,
];

/**
 * Generate a shop inventory of `count` random items.
 * Filters out items that don't apply to the player's current state.
 * Prices are scaled by purchase count.
 */
export function generateShopItems(
  player: PlayerCharacter,
  purchaseCount: number,
  count: number = 10,
): { item: ShopItem; price: number }[] {
  const available = ALL_ENTRIES.filter(e => !e.filter || e.filter(player));

  const weighted: Weighted<ShopEntry>[] = available.map(e => ({
    item: e,
    weight: e.weight,
  }));

  const picked = weightedPickN(weighted, Math.min(count, available.length));
  const multiplier = 1 + purchaseCount * PRICE_ESCALATION;

  return picked.map(e => ({
    item: e.item,
    price: Math.ceil(e.item.basePrice * multiplier),
  }));
}
